// ⌘K palette (prototype G / D+): pages, people search, one action. Radix Dialog for focus + Esc.
import {
  ArrowElbowDownLeft,
  ClockCounterClockwise,
  Copy,
  Gauge,
  House,
  MagnifyingGlass,
  PaperPlaneTilt,
  type Icon as PhIcon,
  SlidersHorizontal,
  Users,
  Wrench,
} from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { Dialog as DialogPrimitive } from "radix-ui";
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Av, Ic, JourneyDots } from "@/components/bits";
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog";
import { EVENT } from "@/lib/fields";
import { isWaiting, matchesSearch } from "@/model/overview";
import { sendCommand } from "@/model/runs";
import { type PStatus, STATUS_LABEL } from "@/model/status";
import { useApp } from "./appData";
import { copyText, useToast } from "./toast";

interface Item {
  group: string;
  label: string;
  sub?: string;
  side?: string;
  icon?: PhIcon;
  avatar?: string;
  status?: PStatus;
  run: () => void;
}

function hl(text: string, q: string): ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { people, counts, next } = useApp();
  const navigate = useNavigate();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  const hlRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
    }
  }, [open]);

  const items = useMemo((): Item[] => {
    const close = (fn: () => void) => () => {
      onOpenChange(false);
      fn();
    };
    const pages: Item[] = [
      { label: "All events", side: "Workspace", icon: House, run: close(() => navigate({ to: "/" })) },
      { label: "Overview", side: "WhatsApp", icon: Gauge, run: close(() => navigate({ to: "/wa" })) },
      {
        label: "People & replies",
        side: counts.waiting ? `${counts.waiting} waiting` : "WhatsApp",
        icon: Users,
        run: close(() => navigate({ to: "/people", search: {} })),
      },
      { label: "Send", side: "WhatsApp", icon: PaperPlaneTilt, run: close(() => navigate({ to: "/send" })) },
      {
        label: "Send history",
        side: "WhatsApp",
        icon: ClockCounterClockwise,
        run: close(() => navigate({ to: "/history" })),
      },
      { label: "Settings", side: "WhatsApp", icon: SlidersHorizontal, run: close(() => navigate({ to: "/settings" })) },
      {
        label: "Needs attention",
        side: "Filter",
        icon: Wrench,
        run: close(() => navigate({ to: "/people", search: { v: "attention" } })),
      },
    ].map((x) => ({ ...x, group: "Pages" }));
    const actions: Item[] = [
      {
        group: "Actions",
        label: "Copy the send command",
        side: next.messages ? `--limit ${next.messages}` : "Nobody waiting",
        icon: Copy,
        run: close(() => {
          copyText(sendCommand(EVENT.slug, next.messages)).then((ok) =>
            toast(ok ? "Send command copied" : "Could not copy; open Send"),
          );
        }),
      },
    ];
    const query = q.trim();
    const found = query ? people.filter((p) => matchesSearch(p, query)).slice(0, 7) : people.filter(isWaiting);
    const ppl: Item[] = found.map((p) => ({
      group: query ? "People" : "Waiting for a reply",
      label: p.name,
      sub: p.regs[0]?.key,
      side: STATUS_LABEL[p.status],
      avatar: p.name,
      status: p.status,
      run: close(() => navigate({ to: "/people", search: { p: p.id } })),
    }));
    const m = (x: Item) => !query || `${x.label} ${x.side ?? ""}`.toLowerCase().includes(query.toLowerCase());
    return [...ppl, ...pages.filter(m), ...actions.filter(m)];
  }, [q, people, counts.waiting, next.messages, navigate, onOpenChange, toast]);

  const cur = Math.min(sel, Math.max(0, items.length - 1));

  // biome-ignore lint/correctness/useExhaustiveDependencies: move the highlight when the selection or list changes
  useLayoutEffect(() => {
    const on = list.current?.querySelector<HTMLElement>(`.ck-i[data-k="${cur}"]`);
    if (!on || !hlRef.current) return;
    hlRef.current.style.transform = `translateY(${on.offsetTop}px)`;
    on.scrollIntoView({ block: "nearest" });
  }, [cur, items]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const n = Math.max(1, items.length);
      setSel((cur + (e.key === "ArrowDown" ? 1 : -1) + n) % n);
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[cur]?.run();
    }
  };

  let group = "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="cmd-overlay" />
        <DialogPrimitive.Content className="cmdk" aria-describedby={undefined} onKeyDown={onKey}>
          <DialogPrimitive.Title className="sr-only">Command menu</DialogPrimitive.Title>
          <div className="ck-in">
            <Ic i={MagnifyingGlass} />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setSel(0);
              }}
              placeholder="Search people, pages, actions…"
              autoComplete="off"
              aria-label="Search"
            />
            <span className="kbd">esc</span>
          </div>
          <div className="ck-list" ref={list} role="listbox">
            {items.length ? <span className="ck-hl" ref={hlRef} /> : null}
            {items.length ? (
              items.map((x, k) => {
                const head = x.group !== group ? <div className="ck-g">{x.group}</div> : null;
                group = x.group;
                return (
                  <div key={`${x.group}-${x.label}-${k}`} style={{ display: "contents" }}>
                    {head}
                    <button
                      type="button"
                      className={`ck-i${k === cur ? " on" : ""}`}
                      role="option"
                      aria-selected={k === cur}
                      data-k={k}
                      onMouseMove={() => k !== cur && setSel(k)}
                      onClick={x.run}
                    >
                      {x.avatar ? (
                        <Av name={x.avatar} />
                      ) : (
                        <span className="ici">{x.icon ? <Ic i={x.icon} /> : null}</span>
                      )}
                      <span className="l">
                        {hl(x.label, q.trim())}
                        {x.sub ? <small className="mono">{hl(x.sub, q.trim())}</small> : null}
                      </span>
                      <span className="s">
                        {x.status ? <JourneyDots s={x.status} /> : null}
                        {x.side}
                      </span>
                      <Ic i={ArrowElbowDownLeft} className="ent" />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="ck-empty">Nothing found for “{q}”</div>
            )}
          </div>
          <div className="ck-foot">
            <span>
              <span className="kbd">↑</span>
              <span className="kbd">↓</span>move
            </span>
            <span>
              <span className="kbd">↵</span>open
            </span>
            <span style={{ marginLeft: "auto" }} className="hide-sm">
              <span className="kbd">⌘K</span>toggle
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
